import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import useStore from '../../components/WorkFlow/store';
import {
  ArrowLeft,
  Sparkles,
  Info,
  GitBranch,
  Bot,
  Pencil,
  Phone,
  Send,
  RotateCcw,
  MessageSquare,
} from 'lucide-react';
import { Card, Badge, PrimaryButton } from './ui';
import { LoadingRows, ErrorNote } from './DemoCustomerAIPages';
import { scriptsAPI, chatbotAPI, scriptKind } from './api';
import { getOutboundCallUrl, getCurrentUserIdForCalls } from '../../lib/apiConfig';
import { AGENT_VOICE_OPTIONS as VOICE_OPTIONS, formatAgentVoiceLabel } from '../../constants/agentVoices';
import WorkflowEdit from '../WorkflowEdit';

const DEFAULT_FROM_NUMBER = '(805) 500-0806';

/* =========================================================
   Script editor — loads a real script from the backend.
   Text Prompt       → editable prompt textarea (saved via /api/update-script)
   Conversation Flow → embeds the legacy react-flow node editor (WorkflowEdit)
                       unchanged; it handles its own node editing + saving.
   ========================================================= */

const NEW_TYPES = {
  'new-prompt': { type: 'Text Prompt', name: 'Untitled Script' },
  'new-workflow': { type: 'Conversation Flow', name: 'Untitled Workflow' },
  'new-ai': { type: 'Text Prompt', name: 'Untitled Script' },
};

const EMPTY_FLOW = { nodes: [], edges: [], agent: {} };

/** Legacy stores Conversation Flow scripts as a JSON string; older docs may hold objects. */
const parseFlow = (raw) => {
  if (raw && typeof raw === 'object') return raw;
  if (typeof raw === 'string' && raw.trim().startsWith('{')) {
    try {
      return JSON.parse(raw);
    } catch {
      return EMPTY_FLOW;
    }
  }
  return EMPTY_FLOW;
};

/* ---------- Prompt editor ---------- */

const PromptEditor = ({ text, setText }) => (
  <Card className="p-6">
    <div className="flex items-center justify-between">
      <div>
        <h2 className="flex items-center gap-1.5 text-[16px] font-semibold">
          System Prompt <Info size={14} className="text-slate-300" />
        </h2>
        <p className="mt-1 text-[13px] text-slate-500">Defines the behavior, tone, and rules for your AI Agent.</p>
      </div>
      <button className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-[13px] font-semibold shadow-sm hover:bg-slate-50">
        <Sparkles size={14} className="text-teal-600" /> Generate with AI
      </button>
    </div>

    <div className="mt-4 rounded-xl border border-slate-200 focus-within:border-teal-500">
      <textarea
        rows={18}
        value={text}
        onChange={(e) => setText(e.target.value)}
        className="w-full resize-none rounded-xl bg-transparent px-5 py-4 text-[14px] leading-relaxed outline-none placeholder:text-slate-400"
        placeholder="Describe how your AI should behave, its tone, and its rules..."
      />
      <div className="px-5 pb-3 text-right text-[12px] text-slate-400">{text.length.toLocaleString()} chars</div>
    </div>
  </Card>
);

/* ---------- Testing tab: chat (top) + voice call (bottom) ---------- */

const ScriptChatPanel = ({ scriptData }) => {
  const [sessionId, setSessionId] = useState('');
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, sending]);

  const start = () => {
    setSessionId(`sess-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`);
    const businessName = scriptData?.businessName || 'our office';
    const aiName = scriptData?.aiName || 'the assistant';
    setMessages([
      { role: 'assistant', content: `Hi there! This is ${aiName} from ${businessName}. How can I help today?` },
    ]);
    setInput('');
  };

  const reset = () => {
    setSessionId('');
    setMessages([]);
    setInput('');
  };

  const send = async () => {
    const q = input.trim();
    if (!q || sending) return;
    setInput('');
    const history = [...messages, { role: 'user', content: q }];
    setMessages(history);
    setSending(true);
    try {
      const res = await chatbotAPI.send({ sessionId, scriptData, fileId: null, query: q, messages: history });
      const data = res.data;
      if (!data?.success) {
        setMessages((m) => [...m, { role: 'assistant', content: `Error: ${data?.message || 'request failed'}` }]);
      } else {
        setMessages((m) => [...m, { role: 'assistant', content: data.reply }]);
      }
    } catch (e) {
      setMessages((m) => [...m, { role: 'assistant', content: `Error: ${e.response?.data?.message || e.message}` }]);
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <div className="flex shrink-0 items-center justify-between border-b border-slate-100 px-5 py-3">
        <h2 className="flex items-center gap-1.5 text-[15px] font-semibold">
          <MessageSquare size={16} className="text-teal-600" /> Test chat
        </h2>
        {sessionId && (
          <button
            onClick={reset}
            className="flex items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5 text-[12px] font-semibold hover:bg-slate-50"
          >
            <RotateCcw size={12} /> Reset
          </button>
        )}
      </div>

      <div ref={scrollRef} className="min-h-0 flex-1 space-y-2.5 overflow-y-auto bg-slate-50/40 px-4 py-4">
        {!sessionId && (
          <div className="flex h-full flex-col items-center justify-center text-center">
            <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-teal-50 text-teal-600">
              <MessageSquare size={22} strokeWidth={1.8} />
            </span>
            <p className="mt-3 text-[13.5px] font-semibold">Start a test chat</p>
            <button
              onClick={start}
              className="mt-3 inline-flex items-center gap-2 rounded-xl bg-teal-600 px-4 py-2 text-[13px] font-semibold text-white shadow-sm hover:bg-teal-700"
            >
              Start session
            </button>
          </div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
            <div
              className={`max-w-[85%] rounded-2xl px-3.5 py-2 text-[13px] leading-relaxed ${
                m.role === 'user'
                  ? 'rounded-br-sm bg-teal-600 text-white'
                  : 'rounded-bl-sm bg-white text-slate-700 shadow-sm ring-1 ring-slate-100'
              }`}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div className="flex justify-start">
            <div className="rounded-2xl rounded-bl-sm bg-white px-3.5 py-2.5 shadow-sm ring-1 ring-slate-100">
              <span className="flex gap-1">
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-300 [animation-delay:-0.3s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-300 [animation-delay:-0.15s]" />
                <span className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-300" />
              </span>
            </div>
          </div>
        )}
      </div>

      {sessionId && (
        <div className="flex shrink-0 items-center gap-2 border-t border-slate-100 px-3 py-2.5">
          <input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && send()}
            placeholder="Type a message…"
            disabled={sending}
            className="flex-1 rounded-xl border border-slate-200 bg-white px-3.5 py-2 text-[13.5px] outline-none focus:border-teal-500 disabled:opacity-60"
          />
          <button
            onClick={send}
            disabled={!input.trim() || sending}
            className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-teal-600 text-white shadow-sm hover:bg-teal-700 disabled:opacity-50"
          >
            <Send size={15} />
          </button>
        </div>
      )}
    </Card>
  );
};

const STT_MODEL_OPTIONS = [
  { value: '', label: 'Default (nova-2-phonecall)' },
  { value: 'nova-2-phonecall', label: 'Deepgram nova-2-phonecall (English)' },
  { value: 'nova-2', label: 'Deepgram nova-2 (single-language, incl. Chinese)' },
  { value: 'nova-3', label: 'Deepgram nova-3 (single-language, incl. Chinese)' },
  { value: 'gpt-transcribe', label: 'OpenAI gpt-transcribe (auto EN+中文 in one call)' },
  { value: 'gpt-4o-transcribe', label: 'OpenAI gpt-4o-transcribe (auto EN+中文, higher accuracy)' },
];
const STT_LANGUAGE_OPTIONS = [
  { value: '', label: 'Default (English)' },
  { value: 'en', label: 'English' },
  { value: 'zh', label: 'Chinese / 中文 (use nova-3)' },
  { value: 'multi', label: 'Multilingual code-switch — EN + European, NO Chinese' },
];
const TTS_MODEL_OPTIONS = [
  { value: '', label: 'Default (Flash v2.5 — fastest)' },
  { value: 'eleven_flash_v2_5', label: 'ElevenLabs Flash v2.5 (fast, multilingual)' },
  { value: 'eleven_turbo_v2_5', label: 'ElevenLabs Turbo v2.5 (balanced)' },
  { value: 'eleven_multilingual_v2', label: 'ElevenLabs Multilingual v2 (higher quality)' },
  { value: 'eleven_v3', label: 'ElevenLabs v3 (highest quality, slower)' },
];

// Cartesia is multilingual — the selected language sets the voice's spoken language.
const TTS_LANGUAGE_OPTIONS = [
  { value: '', label: 'Default (English)' },
  { value: 'en', label: 'English' },
  { value: 'zh', label: 'Chinese (中文)' },
  { value: 'es', label: 'Spanish' },
  { value: 'fr', label: 'French' },
  { value: 'de', label: 'German' },
  { value: 'ja', label: 'Japanese' },
  { value: 'ko', label: 'Korean' },
  { value: 'hi', label: 'Hindi' },
  { value: 'pt', label: 'Portuguese' },
  { value: 'it', label: 'Italian' },
];

const ScriptVoicePanel = ({ scriptData }) => {
  const [destination, setDestination] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [ttsEngine, setTtsEngine] = useState('kokoro'); // 'kokoro' | 'elevenlabs' | 'cartesia'
  const [elevenVoiceId, setElevenVoiceId] = useState(
    (VOICE_OPTIONS.find((v) => v.provider === 'ElevenLabs') || {}).id || ''
  );
  const [cartesiaVoiceId, setCartesiaVoiceId] = useState(
    (VOICE_OPTIONS.find((v) => v.provider === 'Cartesia') || {}).id || ''
  );
  const [testSttModel, setTestSttModel] = useState('');
  const [testSttLanguage, setTestSttLanguage] = useState('');
  const [testTtsModel, setTestTtsModel] = useState('');
  const [testTtsLanguage, setTestTtsLanguage] = useState('');
  const [starting, setStarting] = useState(false);
  const [err, setErr] = useState('');
  const [okMsg, setOkMsg] = useState('');

  const startCall = async () => {
    const to = destination.trim().replace(/\D/g, '');
    if (!to) {
      setErr('Enter a destination phone number.');
      return;
    }
    if (!scriptData) return;
    setStarting(true);
    setErr('');
    setOkMsg('');
    try {
      const token = localStorage.getItem('token');
      const placedByUserId = getCurrentUserIdForCalls();
      const sd = {
        ...scriptData,
        firstName: firstName.trim() || null,
        lastName: lastName.trim() || null,
      };
      const body = {
        to: destination.trim(),
        from: DEFAULT_FROM_NUMBER,
        stack: 'kokoro-vad',
        preferBodyScript: true,
        ...(placedByUserId ? { placedByUserId } : {}),
        scriptData: sd,
        fileId: null,
        voiceId:
          ttsEngine === 'elevenlabs'
            ? elevenVoiceId
            : ttsEngine === 'cartesia'
              ? cartesiaVoiceId
              : scriptData.settings?.voiceId || VOICE_OPTIONS[0].id,
        ttsProvider: ttsEngine,
        ...(ttsEngine === 'elevenlabs' && testTtsModel ? { ttsModel: testTtsModel } : {}),
        ...(ttsEngine === 'cartesia' && testTtsLanguage ? { ttsLanguage: testTtsLanguage } : {}),
        ...(testSttModel ? { sttModel: testSttModel } : {}),
        ...(testSttLanguage ? { sttLanguage: testSttLanguage } : {}),
        useTen: true, // route audio through the TEN framework server (matches legacy "Start call")
      };
      const res = await fetch(getOutboundCallUrl(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify(body),
      });
      const raw = await res.text();
      let data = null;
      try {
        data = raw ? JSON.parse(raw) : null;
      } catch {
        setErr('Server returned an invalid response.');
        return;
      }
      if (res.ok && data?.success) {
        setOkMsg(`Call started. Call SID: ${data.callSid}`);
        setDestination('');
        setFirstName('');
        setLastName('');
      } else {
        setErr(data?.message || 'Failed to start call.');
      }
    } catch (e) {
      setErr(`Error starting call: ${e.message}`);
    } finally {
      setStarting(false);
    }
  };

  const fieldClass =
    'w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-[13.5px] outline-none focus:border-teal-500';

  return (
    <Card className="flex w-full shrink-0 flex-col overflow-hidden">
      <div className="shrink-0 border-b border-slate-100 px-5 py-3">
        <h2 className="flex items-center gap-1.5 text-[15px] font-semibold">
          <Phone size={16} className="text-teal-600" /> Test voice call
        </h2>
      </div>

      <div className="max-h-[46vh] space-y-3.5 overflow-y-auto px-5 py-4">
        <div>
          <label className="mb-1.5 block text-[12.5px] font-semibold text-slate-600">
            Destination number
          </label>
          <input
            type="tel"
            value={destination}
            onChange={(e) => setDestination(e.target.value)}
            placeholder="+1 555 555 1234"
            className={fieldClass}
          />
        </div>

        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="mb-1.5 block text-[12.5px] font-semibold text-slate-600">
              Callee first name
            </label>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)} className={fieldClass} />
          </div>
          <div>
            <label className="mb-1.5 block text-[12.5px] font-semibold text-slate-600">
              Callee last name
            </label>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)} className={fieldClass} />
          </div>
        </div>

        <div>
          <label className="mb-1.5 block text-[12.5px] font-semibold text-slate-600">Voice engine</label>
          <select
            value={ttsEngine}
            onChange={(e) => setTtsEngine(e.target.value)}
            className={`${fieldClass} appearance-none`}
          >
            <option value="kokoro">Kokoro (fast, default)</option>
            <option value="elevenlabs">ElevenLabs (higher quality)</option>
            <option value="cartesia">Cartesia Sonic (multilingual)</option>
          </select>
        </div>

        {ttsEngine === 'cartesia' && (
          <div>
            <label className="mb-1.5 block text-[12.5px] font-semibold text-slate-600">
              Cartesia voice
            </label>
            <select
              value={cartesiaVoiceId}
              onChange={(e) => setCartesiaVoiceId(e.target.value)}
              className={`${fieldClass} appearance-none`}
            >
              {VOICE_OPTIONS.filter((v) => v.provider === 'Cartesia').map((v) => (
                <option key={v.id} value={v.id}>
                  {formatAgentVoiceLabel(v)}
                </option>
              ))}
            </select>
            <label className="mb-1.5 mt-3 block text-[12.5px] font-semibold text-slate-600">
              Cartesia language
            </label>
            <select
              value={testTtsLanguage}
              onChange={(e) => setTestTtsLanguage(e.target.value)}
              className={`${fieldClass} appearance-none`}
            >
              {TTS_LANGUAGE_OPTIONS.map((o) => (
                <option key={o.value || 'default'} value={o.value}>{o.label}</option>
              ))}
            </select>
            <p className="text-[11.5px] text-slate-400 mt-1">Cartesia voices are multilingual — the language sets what the voice speaks.</p>
          </div>
        )}

        {ttsEngine === 'elevenlabs' && (
          <div>
            <label className="mb-1.5 block text-[12.5px] font-semibold text-slate-600">
              ElevenLabs voice
            </label>
            <select
              value={elevenVoiceId}
              onChange={(e) => setElevenVoiceId(e.target.value)}
              className={`${fieldClass} appearance-none`}
            >
              {VOICE_OPTIONS.filter((v) => v.provider === 'ElevenLabs').map((v) => (
                <option key={v.id} value={v.id}>
                  {formatAgentVoiceLabel(v)}
                </option>
              ))}
            </select>
            <label className="mb-1.5 mt-3 block text-[12.5px] font-semibold text-slate-600">
              ElevenLabs model
            </label>
            <select
              value={testTtsModel}
              onChange={(e) => setTestTtsModel(e.target.value)}
              className={`${fieldClass} appearance-none`}
            >
              {TTS_MODEL_OPTIONS.map((o) => (
                <option key={o.value || 'default'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-2.5">
          <div>
            <label className="mb-1.5 block text-[12.5px] font-semibold text-slate-600">Speech recognition</label>
            <select
              value={testSttModel}
              onChange={(e) => setTestSttModel(e.target.value)}
              className={`${fieldClass} appearance-none`}
            >
              {STT_MODEL_OPTIONS.map((o) => (
                <option key={o.value || 'default'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1.5 block text-[12.5px] font-semibold text-slate-600">STT language</label>
            <select
              value={testSttLanguage}
              onChange={(e) => setTestSttLanguage(e.target.value)}
              className={`${fieldClass} appearance-none`}
            >
              {STT_LANGUAGE_OPTIONS.map((o) => (
                <option key={o.value || 'default'} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
        </div>
        <p className="text-[11.5px] text-slate-400">For live EN+中文 in one call, pick OpenAI gpt-transcribe. Chinese-only: nova-3 + Chinese.</p>

        {err && (
          <div className="rounded-xl border border-red-100 bg-red-50 px-3.5 py-2.5 text-[12.5px] text-red-600">
            {err}
          </div>
        )}
        {okMsg && (
          <div className="rounded-xl border border-emerald-100 bg-emerald-50 px-3.5 py-2.5 text-[12.5px] text-emerald-700">
            {okMsg}
          </div>
        )}
      </div>

      <div className="shrink-0 border-t border-slate-100 px-5 py-4">
        <button
          onClick={startCall}
          disabled={!destination.trim() || starting}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-teal-600 px-5 py-3 text-[14px] font-semibold text-white shadow-sm hover:bg-teal-700 disabled:opacity-50"
        >
          <Phone size={16} /> {starting ? 'Starting…' : 'Start call'}
        </button>
        <p className="mt-2 text-center text-[11.5px] text-slate-400">From: {DEFAULT_FROM_NUMBER}</p>
      </div>
    </Card>
  );
};

const ScriptTester = ({ scriptData }) => (
  <div className="flex h-full min-h-0 flex-col gap-4">
    <ScriptChatPanel scriptData={scriptData} />
    <ScriptVoicePanel scriptData={scriptData} />
  </div>
);

/* ---------- page ---------- */

const DemoScriptEditor = () => {
  const { scriptId } = useParams();
  const navigate = useNavigate();
  const isNew = Boolean(NEW_TYPES[scriptId]);

  const [doc, setDoc] = useState(null);
  const [error, setError] = useState(null);
  const [name, setName] = useState(isNew ? NEW_TYPES[scriptId].name : '');
  const [text, setText] = useState('');
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [autoStatus, setAutoStatus] = useState('idle'); // 'idle' | 'saving' | 'saved' | 'error'
  const [editingName, setEditingName] = useState(false);
  /** Skips the autosave that would otherwise fire right after a script loads. */
  const autosaveInit = useRef(true);
  /** Bumped after loads so WorkflowEdit reseeds its canvas only then. */
  const [loadKey, setLoadKey] = useState('');

  useEffect(() => {
    setError(null);
    setSavedAt(null);
    if (isNew) {
      setDoc({ type: NEW_TYPES[scriptId].type, script: '' });
      setName(NEW_TYPES[scriptId].name);
      setText('');
      setLoadKey(`${scriptId}-${Date.now()}`);
      return;
    }
    setDoc(null);
    scriptsAPI
      .get(scriptId)
      .then((res) => {
        const s = res.data.script;
        setDoc(s);
        setName(s.scriptName || 'Untitled');
        setText(typeof s.script === 'string' && !s.script.trim().startsWith('{') ? s.script : '');
        setLoadKey(`${scriptId}-${Date.now()}`);
      })
      .catch((e) => setError(e.response?.data?.error || e.message));
  }, [scriptId, isNew]);

  const kind = doc ? scriptKind(doc.type) : 'Prompt';
  const flow = useMemo(
    () => (doc && kind === 'Workflow' ? parseFlow(doc.script) : EMPTY_FLOW),
    [doc, kind]
  );

  /** Shape the in-editor script for the /api/chatbot test endpoint (mirrors legacy scriptDataForTry). */
  const scriptData = useMemo(
    () =>
      doc
        ? {
            _id: isNew ? 'draft' : doc._id,
            scriptName: name || 'Untitled Script',
            script: kind === 'Workflow' ? doc.script : text,
            type: doc.type,
          }
        : null,
    [doc, isNew, name, text, kind]
  );

  /* Reset the autosave guard whenever a different script loads. */
  useEffect(() => {
    autosaveInit.current = true;
    setAutoStatus('idle');
  }, [loadKey]);

  /* Auto-save existing scripts (debounced): name (both kinds) + prompt text.
     Workflow node/edge auto-save via the store subscription below. */
  useEffect(() => {
    if (isNew || !doc?._id) return;
    if (autosaveInit.current) {
      autosaveInit.current = false;
      return;
    }
    setAutoStatus('saving');
    const t = setTimeout(async () => {
      try {
        const payload = { _id: doc._id, scriptName: name || 'Untitled' };
        if (kind === 'Prompt') {
          payload.script = text;
        }
        await scriptsAPI.update(payload);
        setAutoStatus('saved');
        setSavedAt(new Date());
      } catch {
        setAutoStatus('error');
      }
    }, 800);
    return () => clearTimeout(t);
  }, [name, text, isNew, doc, kind]);

  /* Debounced workflow save shared by canvas edits (onWorkflowChange) and agent-setting edits. */
  const wfSaveTimer = useRef(null);
  const scheduleWorkflowSave = useCallback(
    (json) => {
      if (isNew || !doc?._id) return;
      setAutoStatus('saving');
      clearTimeout(wfSaveTimer.current);
      wfSaveTimer.current = setTimeout(async () => {
        try {
          await scriptsAPI.update({ _id: doc._id, script: json });
          setAutoStatus('saved');
          setSavedAt(new Date());
        } catch {
          setAutoStatus('error');
        }
      }, 1500);
    },
    [isNew, doc]
  );

  const handleWorkflowChange = useCallback((json) => scheduleWorkflowSave(json), [scheduleWorkflowSave]);

  /* Auto-save workflow agent settings: onWorkflowChange only fires on node/edge edits, so
     subscribe to the store's agentSetting separately and save when it changes post-load. */
  useEffect(() => {
    if (isNew || !doc?._id || kind !== 'Workflow') return;
    let armed = false;
    let prevAgent = JSON.stringify(useStore.getState().agentSetting);
    const armTimer = setTimeout(() => {
      armed = true;
      prevAgent = JSON.stringify(useStore.getState().agentSetting);
    }, 700);
    const unsub = useStore.subscribe((state) => {
      if (!armed) return;
      const cur = JSON.stringify(state.agentSetting);
      if (cur === prevAgent) return; // ignore node/edge-only changes (handled by onWorkflowChange)
      prevAgent = cur;
      const json = JSON.stringify(
        { nodes: state.nodes, edges: state.edges, agent: state.agentSetting },
        null,
        2
      );
      scheduleWorkflowSave(json);
    });
    return () => {
      unsub();
      clearTimeout(armTimer);
    };
  }, [isNew, doc, kind, scheduleWorkflowSave]);

  /** New workflow scripts: legacy editor's save calls this until the script exists. */
  const handleWorkflowDraftSave = async (workflowJson) => {
    try {
      const res = await scriptsAPI.save({
        scriptName: name || 'Untitled Workflow',
        script: JSON.stringify(workflowJson, null, 2),
        type: 'Conversation Flow',
      });
      if (res.data.id) {
        navigate(`/dashboard/customer-ai/scripts/${res.data.id}`, { replace: true });
      }
    } catch (e) {
      alert(`Save failed: ${e.response?.data?.error || e.message}`);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      if (isNew) {
        if (kind === 'Workflow') {
          await handleWorkflowDraftSave(EMPTY_FLOW);
          return;
        }
        const res = await scriptsAPI.save({
          scriptName: name || 'Untitled Script',
          script: text,
          type: doc.type,
        });
        if (res.data.id) {
          navigate(`/dashboard/customer-ai/scripts/${res.data.id}`, { replace: true });
        } else {
          navigate('/dashboard/customer-ai/scripts');
        }
      } else {
        const payload = { _id: doc._id, scriptName: name };
        if (kind === 'Prompt') payload.script = text;
        await scriptsAPI.update(payload);
        setSavedAt(new Date());
      }
    } catch (e) {
      alert(`Save failed: ${e.response?.data?.error || e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex shrink-0 items-end justify-between pt-4">
        <div>
          <button
            onClick={() => navigate('/dashboard/customer-ai/scripts')}
            className="flex items-center gap-1.5 text-[13px] font-semibold text-slate-500 hover:text-slate-800"
          >
            <ArrowLeft size={15} /> Scripts
          </button>
          <div className="mt-2 flex items-center gap-3">
            <span
              className={`flex h-11 w-11 items-center justify-center rounded-xl ${
                kind === 'Workflow' ? 'bg-violet-50 text-violet-600' : 'bg-sky-50 text-sky-600'
              }`}
            >
              {kind === 'Workflow' ? <GitBranch size={21} strokeWidth={1.9} /> : <Bot size={21} strokeWidth={1.9} />}
            </span>
            <div>
              {editingName ? (
                <input
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  onBlur={() => setEditingName(false)}
                  onKeyDown={(e) => (e.key === 'Enter' || e.key === 'Escape') && setEditingName(false)}
                  className="w-[420px] rounded-lg bg-white px-2 py-0.5 text-[24px] font-bold tracking-tight outline-none ring-1 ring-teal-500"
                />
              ) : (
                <div className="flex items-center gap-1.5">
                  <h1 className="max-w-[520px] truncate text-[24px] font-bold tracking-tight">
                    {name || 'Untitled'}
                  </h1>
                  <button
                    onClick={() => setEditingName(true)}
                    title="Rename script"
                    className="rounded-lg p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                  >
                    <Pencil size={16} />
                  </button>
                </div>
              )}
              <div className="mt-0.5 flex items-center gap-2">
                <Badge tone={kind === 'Workflow' ? 'purple' : 'blue'}>{kind}</Badge>
                {doc?.createdAt && <Badge tone="gray">Created {new Date(doc.createdAt).toLocaleDateString()}</Badge>}
                {!isNew && autoStatus === 'saving' && <Badge tone="gray">Saving…</Badge>}
                {!isNew && autoStatus === 'saved' && <Badge tone="green">Saved</Badge>}
                {!isNew && autoStatus === 'error' && <Badge tone="red">Save failed</Badge>}
              </div>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {isNew ? (
            <PrimaryButton onClick={handleSave} disabled={saving}>
              {saving ? 'Saving…' : 'Create Script'}
            </PrimaryButton>
          ) : (
            <span className="text-[12.5px] font-medium text-slate-400">
              {autoStatus === 'saving' ? 'Saving…' : 'All changes saved automatically'}
            </span>
          )}
        </div>
      </div>

      {error && <div className="mt-6"><ErrorNote error={error} /></div>}
      {!error && !doc && <LoadingRows label="Loading script…" />}

      {/* 75% Script Builder + 25% Testing. Basic settings / voice / knowledge now live on
          the Agent entity (Customer AI › Agents), not on the script. */}
      {!error && doc && (
        <div className="demo-scroll mt-5 grid min-h-0 flex-1 grid-cols-1 gap-5 overflow-y-auto pb-1 xl:grid-cols-[3fr_1fr] xl:overflow-hidden">
          {/* Script Builder */}
          <div className="min-h-[440px] overflow-hidden xl:min-h-0">
            {kind === 'Workflow' ? (
              <Card className="flex h-full min-h-0 flex-col overflow-hidden">
                <div className="flex min-h-0 flex-1">
                  <div className="flex min-w-0 flex-1 flex-col">
                    <WorkflowEdit
                      initialData={flow}
                      workflowLoadKey={loadKey}
                      scriptId={isNew ? null : doc._id}
                      hideAgentSettings
                      hideSaveButton={!isNew}
                      onSaveDraft={handleWorkflowDraftSave}
                      onWorkflowChange={isNew ? undefined : handleWorkflowChange}
                    />
                  </div>
                </div>
              </Card>
            ) : (
              <div className="h-full min-h-0 overflow-y-auto">
                <PromptEditor text={text} setText={setText} />
              </div>
            )}
          </div>

          {/* Testing panel */}
          <div className="flex min-h-[440px] flex-col gap-3 overflow-hidden xl:min-h-0">
            <div className="flex shrink-0 items-center gap-1 rounded-xl border border-slate-200 bg-white p-1 shadow-sm">
              <span className="flex-1 rounded-lg bg-teal-50 px-3 py-2 text-center text-[13px] font-semibold text-teal-700">
                Testing
              </span>
            </div>
            <div className="min-h-0 flex-1 overflow-hidden">
              <ScriptTester scriptData={scriptData} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DemoScriptEditor;
